import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Trash2, ArrowUp, ArrowDown, History, Save, Eye, Monitor, Laptop, Tablet, Smartphone } from "lucide-react";

const STATUS_COLORS = { draft: "bg-gold/20 text-charcoal", pending_review: "bg-saffron/20 text-saffron", published: "bg-forest/15 text-forest", unpublished: "bg-muted text-muted-foreground", archived: "bg-maroon/10 text-maroon" };
const STATUS_LABEL = { draft: "Draft", pending_review: "Pending Review", published: "Published", unpublished: "Unpublished", archived: "Archived" };
const DEVICES = [["desktop", "Desktop", Monitor, 1280], ["laptop", "Laptop", Laptop, 1024], ["tablet", "Tablet", Tablet, 768], ["mobile", "Mobile", Smartphone, 390]];

const arrMove = (arr, i, j) => { const a = [...arr]; const [x] = a.splice(i, 1); a.splice(j, 0, x); return a; };

function LinkRow({ ln, onChange, onRemove, onUp, onDown, canUp, canDown, testid }) {
  return (
    <div className="flex items-center gap-2" data-testid={testid}>
      <Input className="flex-1" placeholder="Label" value={ln.label || ""} onChange={(e) => onChange({ ...ln, label: e.target.value })} data-testid={`${testid}-label`} />
      <Input className="flex-1" placeholder="/path or https://…" value={ln.href || ""} onChange={(e) => onChange({ ...ln, href: e.target.value })} data-testid={`${testid}-href`} />
      <Button size="icon" variant="ghost" disabled={!canUp} onClick={onUp} data-testid={`${testid}-up`}><ArrowUp className="h-4 w-4" /></Button>
      <Button size="icon" variant="ghost" disabled={!canDown} onClick={onDown} data-testid={`${testid}-down`}><ArrowDown className="h-4 w-4" /></Button>
      <Button size="icon" variant="ghost" onClick={onRemove} data-testid={`${testid}-remove`}><Trash2 className="h-4 w-4 text-maroon" /></Button>
    </div>
  );
}

// ---------- Device preview renderers (authenticated, static approximations) ----------
function HeaderPreview({ data, narrow }) {
  const nav = data.nav_links || [];
  const cta = data.cta || {};
  const active = (it) => ((it && it.children) || []).filter((c) => c.active !== false && (c.href || "").trim());
  return (
    <div className="border-b border-gold/20 bg-cream/95 px-4 py-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2"><div className="flex h-8 w-8 items-center justify-center rounded-full bg-maroon text-cream font-display text-sm font-bold">M</div><span className="font-display text-base font-bold text-charcoal">Maha Sampada</span></div>
        {narrow ? <div className="text-charcoal">☰</div> : (
          <div className="flex items-center gap-4">
            {nav.map((n, i) => <span key={i} className="font-accent text-[12px] text-charcoal">{n.label}{active(n).length > 0 ? " ▾" : ""}</span>)}
            {cta.enabled !== false && cta.label && <span className="rounded-full bg-saffron px-4 py-1.5 font-accent text-xs font-semibold text-white">{cta.label}</span>}
          </div>
        )}
      </div>
      {narrow ? (
        <div className="mt-2 space-y-1" data-testid="header-preview-mobile-nav">
          {nav.map((n, i) => (
            <div key={i} className="text-[11px] text-charcoal">
              {n.label}{active(n).length > 0 ? " ▾" : ""}
              {active(n).length > 0 && <div className="pl-3 text-muted-foreground">{active(n).map((c) => c.label).join(", ")}</div>}
            </div>
          ))}
        </div>
      ) : nav.some((n) => active(n).length > 0) && (
        <div className="mt-2 flex flex-wrap gap-4 border-t border-gold/10 pt-2">
          {nav.filter((n) => active(n).length > 0).map((n, i) => (
            <div key={i} className="text-[11px] text-muted-foreground"><span className="font-semibold text-charcoal">{n.label}:</span> {active(n).map((c) => c.label).join(" · ")}</div>
          ))}
        </div>
      )}
    </div>
  );
}
function FooterPreview({ data }) {
  const cols = data.columns || [];
  const legal = data.legal_links || [];
  return (
    <div className="bg-charcoal px-4 py-6 text-cream/80">
      <div className="grid grid-cols-2 gap-6 md:grid-cols-4">
        <div><div className="font-display text-lg font-bold text-cream">Maha Sampada</div><p className="mt-2 text-[11px] leading-relaxed text-cream/70 line-clamp-3">{data.tagline}</p></div>
        {cols.map((c, i) => (<div key={i}><h4 className="overline text-gold text-[10px]">{c.title}</h4><ul className="mt-2 space-y-1 text-[11px]">{(c.links || []).map((l, j) => <li key={j}>{l.label}</li>)}</ul></div>))}
        {data.show_contact !== false && <div><h4 className="overline text-gold text-[10px]">Contact</h4><p className="mt-2 text-[11px] text-cream/60">From Global Settings</p></div>}
      </div>
      <div className="mt-6 flex flex-wrap items-center justify-between gap-2 border-t border-cream/15 pt-3 text-[11px] text-cream/60">
        <div className="flex flex-wrap gap-3">{legal.map((l, i) => <span key={i}>{l.label}</span>)}</div>
        <div>{(data.copyright || "").replace("{year}", new Date().getFullYear())}</div>
      </div>
    </div>
  );
}

export function LayoutManager({ which, canPublish }) {
  const [section, setSection] = useState(null);
  const [data, setData] = useState(null);
  const [note, setNote] = useState("");
  const [dirty, setDirty] = useState(false);
  const [revs, setRevs] = useState(null);
  const [previewDevice, setPreviewDevice] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data: secs } = await api.get("/admin/cms/sections", { params: { page: "global" } });
      const s = secs.find((x) => x.section_id === which);
      setSection(s || null);
      setData(s?.data ? JSON.parse(JSON.stringify(s.data)) : (which === "header" ? { nav_links: [], cta: { label: "", href: "", enabled: true } } : { tagline: "", columns: [], legal_links: [], copyright: "", show_contact: true, show_social: true }));
      setDirty(false); setNote("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, [which]);
  useEffect(() => { load(); }, [load]);

  const up = (patch) => { setData((d) => ({ ...d, ...patch })); setDirty(true); };

  const save = async () => {
    try {
      const { data: res } = await api.put(`/admin/cms/sections/${section.id}`, { data, change_note: note });
      toast.success(`Draft saved (v${res.version})`); setNote(""); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const setStatus = async (status) => {
    if (dirty) { toast.error("Save your draft before changing its status."); return; }
    try { await api.post(`/admin/cms/sections/${section.id}/status`, { status }); toast.success(`Marked ${STATUS_LABEL[status]}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const openRevs = async () => { const { data: list } = await api.get(`/admin/cms/sections/${section.id}/revisions`); setRevs(list); };
  const restore = async (v) => { try { await api.post(`/admin/cms/sections/${section.id}/restore/${v}`); toast.success("Restored as new draft version"); setRevs(null); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  if (!section || !data) return <div className="rounded-xl border border-border bg-white p-6 text-sm text-muted-foreground" data-testid={`layout-${which}-empty`}>Loading {which}…</div>;

  const liveDiffers = JSON.stringify(data) !== JSON.stringify(section.published_data ?? null);
  const isPublished = section.status === "published";

  return (
    <div className="space-y-4" data-testid={`layout-manager-${which}`}>
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-white p-4">
        <div className="flex items-center gap-3">
          <h3 className="font-display text-lg font-semibold text-charcoal">{which === "header" ? "Header Manager" : "Footer Manager"}</h3>
          <Badge className={`border-0 ${STATUS_COLORS[section.status]}`}>{STATUS_LABEL[section.status]}</Badge>
          <span className="text-xs text-muted-foreground">v{section.version}</span>
          {dirty && <span className="text-xs font-semibold text-saffron">Unsaved changes</span>}
          {!dirty && liveDiffers && isPublished && <span className="text-xs font-semibold text-saffron" data-testid={`layout-${which}-ahead`}>Draft ahead of live — publish to go live</span>}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={openRevs} data-testid={`layout-${which}-revs`} className="rounded-full text-xs"><History className="mr-1 h-3.5 w-3.5" />History</Button>
          {DEVICES.map(([k, l, Icon]) => <Button key={k} size="sm" variant="outline" onClick={() => setPreviewDevice(k)} data-testid={`layout-${which}-preview-${k}`} className="rounded-full text-xs"><Icon className="mr-1 h-3.5 w-3.5" />{l}</Button>)}
        </div>
      </div>

      {which === "header" ? <HeaderEditor data={data} up={up} /> : <FooterEditor data={data} up={up} />}

      <div className="rounded-xl border border-border bg-white p-4">
        <Label className="text-xs font-semibold">Change note (optional)</Label>
        <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Added Blog link to header" data-testid={`layout-${which}-note`} className="mt-1" />
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Button onClick={save} data-testid={`layout-${which}-save`} className="rounded-full bg-saffron text-white hover:bg-saffron-dark"><Save className="mr-1 h-4 w-4" />Save Draft (new version)</Button>
          {canPublish && (
            <Button size="sm" onClick={() => setStatus("published")} data-testid={`layout-${which}-status-published`} className="rounded-full bg-forest text-white hover:bg-forest/90 text-xs">{isPublished ? "Publish changes" : "Publish"}</Button>
          )}
          {!isPublished && section.status !== "pending_review" && (
            <Button size="sm" variant="outline" onClick={() => setStatus("pending_review")} data-testid={`layout-${which}-status-pending_review`} className="rounded-full text-xs">Submit for Review</Button>
          )}
          {isPublished && (
            <Button size="sm" variant="outline" onClick={() => setStatus("unpublished")} data-testid={`layout-${which}-status-unpublished`} className="rounded-full text-xs">Unpublish</Button>
          )}
        </div>
        <p className="mt-2 text-xs text-muted-foreground">The live site shows the <strong>Published</strong> version only. Save a draft, preview it on any device, then <strong>Publish</strong> to push it live — the live {which} stays up throughout.</p>
      </div>

      {/* Device preview */}
      <Dialog open={!!previewDevice} onOpenChange={(o) => !o && setPreviewDevice(null)}>
        <DialogContent className="max-w-[95vw]" data-testid={`layout-${which}-preview-dialog`}>
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Eye className="h-5 w-5" />{which === "header" ? "Header" : "Footer"} preview — {DEVICES.find((d) => d[0] === previewDevice)?.[1]} <span className="text-xs font-normal text-muted-foreground">(draft, not indexable)</span></DialogTitle></DialogHeader>
          <div className="overflow-x-auto rounded-lg border border-border bg-muted/30 p-4">
            <div className="mx-auto border border-border shadow-sm" style={{ width: Math.min(DEVICES.find((d) => d[0] === previewDevice)?.[3] || 1024, 1200) }}>
              {which === "header" ? <HeaderPreview data={data} narrow={previewDevice === "mobile"} /> : <FooterPreview data={data} />}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Revisions */}
      <Dialog open={!!revs} onOpenChange={(o) => !o && setRevs(null)}>
        <DialogContent data-testid={`layout-${which}-revs-dialog`}><DialogHeader><DialogTitle className="font-display text-xl">Revision History</DialogTitle></DialogHeader>
          <div className="max-h-80 space-y-1 overflow-y-auto">
            {(!revs || revs.length === 0) && <p className="text-sm text-muted-foreground">No prior revisions yet.</p>}
            {revs?.map((r) => (
              <div key={r.id} className="flex items-center justify-between rounded-lg border border-border px-3 py-2 text-sm" data-testid={`layout-${which}-rev-${r.version}`}>
                <span>v{r.version} · {(r.at || "").slice(0, 16).replace("T", " ")} · {r.by_name}{r.note ? ` · ${r.note}` : ""}</span>
                <Button size="sm" variant="outline" onClick={() => restore(r.version)} data-testid={`layout-${which}-restore-${r.version}`} className="rounded-full text-xs">Restore</Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const REG_PROTECTED = ["/partner", "/service-provider-registration"];

function ChildRow({ c, testid, onChange, onRemove, onUp, onDown, canUp, canDown }) {
  const isExternal = (c.href || "").trim().toLowerCase().startsWith("https://");
  const isProtected = REG_PROTECTED.includes((c.href || "").trim());
  const confirmProtected = (action) => !isProtected || window.confirm(`This child links to ${c.href}, a registration route. Are you sure you want to ${action} it? Visitors may lose easy access to that form.`);
  return (
    <div className="rounded-lg border border-border bg-cream/40 p-2" data-testid={testid}>
      <div className="grid gap-2 sm:grid-cols-2">
        <Input placeholder="Internal admin name" value={c.name || ""} onChange={(e) => onChange({ ...c, name: e.target.value })} data-testid={`${testid}-name`} />
        <Input placeholder="Public label" value={c.label || ""} onChange={(e) => onChange({ ...c, label: e.target.value })} data-testid={`${testid}-label`} />
      </div>
      <div className="mt-2 flex items-center gap-2">
        <Input className="flex-1" placeholder="/path, #section or https://…" value={c.href || ""} onChange={(e) => { const href = e.target.value; const ext = href.trim().toLowerCase().startsWith("https://"); onChange({ ...c, href, new_tab: ext ? c.new_tab : false }); }} data-testid={`${testid}-href`} />
        <Button size="icon" variant="ghost" disabled={!canUp} onClick={onUp} data-testid={`${testid}-up`}><ArrowUp className="h-4 w-4" /></Button>
        <Button size="icon" variant="ghost" disabled={!canDown} onClick={onDown} data-testid={`${testid}-down`}><ArrowDown className="h-4 w-4" /></Button>
        <Button size="icon" variant="ghost" onClick={() => { if (confirmProtected("remove")) onRemove(); }} data-testid={`${testid}-remove`}><Trash2 className="h-4 w-4 text-maroon" /></Button>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-4 text-xs">
        <label className="flex items-center gap-1.5"><input type="checkbox" checked={c.active !== false} onChange={(e) => { if (e.target.checked || confirmProtected("hide")) onChange({ ...c, active: e.target.checked }); }} data-testid={`${testid}-active`} /> Active</label>
        <label className={`flex items-center gap-1.5 ${isExternal ? "" : "opacity-40"}`}><input type="checkbox" disabled={!isExternal} checked={!!c.new_tab && isExternal} onChange={(e) => onChange({ ...c, new_tab: e.target.checked })} data-testid={`${testid}-newtab`} /> Open in new tab (external only)</label>
      </div>
    </div>
  );
}

function HeaderNavItemRow({ item, testid, onChange, onRemove, onUp, onDown, canUp, canDown }) {
  const kids = item.children || [];
  const isDD = Array.isArray(item.children);
  const setKids = (k) => onChange({ ...item, children: k });
  const toStandard = () => { if (!kids.length || window.confirm("Convert to a standard link? This removes all child links in the draft.")) { const { children, ...rest } = item; onChange(rest); } };
  return (
    <div className="rounded-lg border border-border bg-white p-3" data-testid={testid}>
      <div className="flex items-center gap-2">
        <Input className="flex-1" placeholder="Label" value={item.label || ""} onChange={(e) => onChange({ ...item, label: e.target.value })} data-testid={`${testid}-label`} />
        <Input className="flex-1" placeholder="/path or https://…" value={item.href || ""} onChange={(e) => onChange({ ...item, href: e.target.value })} data-testid={`${testid}-href`} />
        <Button size="icon" variant="ghost" disabled={!canUp} onClick={onUp} data-testid={`${testid}-up`}><ArrowUp className="h-4 w-4" /></Button>
        <Button size="icon" variant="ghost" disabled={!canDown} onClick={onDown} data-testid={`${testid}-down`}><ArrowDown className="h-4 w-4" /></Button>
        <Button size="icon" variant="ghost" onClick={onRemove} data-testid={`${testid}-remove`}><Trash2 className="h-4 w-4 text-maroon" /></Button>
      </div>
      <div className="mt-2 flex items-center gap-3">
        {isDD ? (
          <Button size="sm" variant="outline" onClick={toStandard} data-testid={`${testid}-to-standard`} className="rounded-full text-xs">Make standard link</Button>
        ) : (
          <Button size="sm" variant="outline" onClick={() => onChange({ ...item, children: [] })} data-testid={`${testid}-to-dropdown`} className="rounded-full text-xs">Make dropdown</Button>
        )}
        {isDD && <span className="text-xs text-muted-foreground">{kids.filter((c) => c.active !== false).length} active child link(s) · parent destination optional</span>}
      </div>
      {isDD && (
        <div className="mt-3 space-y-2 border-l-2 border-gold/30 pl-3" data-testid={`${testid}-children`}>
          {kids.length === 0 && <p className="text-xs text-muted-foreground">No child links yet — an empty dropdown renders as a normal link.</p>}
          {kids.map((c, ci) => (
            <ChildRow key={ci} c={c} testid={`${testid}-child-${ci}`}
              onChange={(v) => setKids(kids.map((x, j) => j === ci ? v : x))}
              onRemove={() => setKids(kids.filter((_, j) => j !== ci))}
              onUp={() => setKids(arrMove(kids, ci, ci - 1))} onDown={() => setKids(arrMove(kids, ci, ci + 1))}
              canUp={ci > 0} canDown={ci < kids.length - 1} />
          ))}
          <Button size="sm" variant="outline" onClick={() => setKids([...kids, { name: "", label: "", href: "/", active: true, new_tab: false }])} data-testid={`${testid}-add-child`} className="rounded-full text-xs"><Plus className="mr-1 h-3.5 w-3.5" />Add child link</Button>
        </div>
      )}
    </div>
  );
}

function HeaderEditor({ data, up }) {
  const nav = data.nav_links || [];
  const cta = data.cta || { label: "", href: "", enabled: true };
  const setNav = (n) => up({ nav_links: n });
  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-border bg-white p-4">
        <div className="flex items-center justify-between"><h4 className="font-accent text-sm font-bold uppercase text-maroon">Navigation Links</h4>
          <Button size="sm" variant="outline" onClick={() => setNav([...nav, { label: "", href: "/" }])} data-testid="header-add-nav" className="rounded-full text-xs"><Plus className="mr-1 h-3.5 w-3.5" />Add link</Button></div>
        <div className="mt-3 space-y-2">
          {nav.length === 0 && <p className="text-sm text-muted-foreground">No links yet.</p>}
          {nav.map((ln, i) => (
            <HeaderNavItemRow key={i} item={ln} testid={`header-nav-${i}`}
              onChange={(v) => setNav(nav.map((x, j) => j === i ? v : x))}
              onRemove={() => setNav(nav.filter((_, j) => j !== i))}
              onUp={() => setNav(arrMove(nav, i, i - 1))} onDown={() => setNav(arrMove(nav, i, i + 1))}
              canUp={i > 0} canDown={i < nav.length - 1} />
          ))}
        </div>
      </div>
      <div className="rounded-xl border border-border bg-white p-4">
        <h4 className="font-accent text-sm font-bold uppercase text-maroon">Primary CTA</h4>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <div><Label className="text-xs font-semibold">Label</Label><Input value={cta.label || ""} onChange={(e) => up({ cta: { ...cta, label: e.target.value } })} data-testid="header-cta-label" /></div>
          <div><Label className="text-xs font-semibold">Destination</Label><Input value={cta.href || ""} onChange={(e) => up({ cta: { ...cta, href: e.target.value } })} placeholder="/enquiry" data-testid="header-cta-href" /></div>
        </div>
        <label className="mt-3 flex items-center gap-2 text-sm"><input type="checkbox" checked={cta.enabled !== false} onChange={(e) => up({ cta: { ...cta, enabled: e.target.checked } })} data-testid="header-cta-enabled" /> Show CTA button</label>
      </div>
      <p className="text-xs text-muted-foreground">Logo & brand assets are managed in <strong>Global Settings</strong> and shown here automatically.</p>
    </div>
  );
}

function FooterEditor({ data, up }) {
  const cols = data.columns || [];
  const legal = data.legal_links || [];
  const setCols = (c) => up({ columns: c });
  const setColLinks = (ci, links) => setCols(cols.map((c, j) => j === ci ? { ...c, links } : c));
  const setLegal = (l) => up({ legal_links: l });
  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-border bg-white p-4">
        <Label className="text-xs font-semibold">Tagline</Label>
        <Textarea value={data.tagline || ""} onChange={(e) => up({ tagline: e.target.value })} data-testid="footer-tagline" className="mt-1" rows={2} />
      </div>

      <div className="rounded-xl border border-border bg-white p-4">
        <div className="flex items-center justify-between"><h4 className="font-accent text-sm font-bold uppercase text-maroon">Link Columns</h4>
          <Button size="sm" variant="outline" onClick={() => setCols([...cols, { title: "New Column", links: [] }])} data-testid="footer-add-col" className="rounded-full text-xs"><Plus className="mr-1 h-3.5 w-3.5" />Add column</Button></div>
        <div className="mt-3 space-y-4">
          {cols.map((col, ci) => (
            <div key={ci} className="rounded-lg border border-border bg-cream/30 p-3" data-testid={`footer-col-${ci}`}>
              <div className="flex items-center gap-2">
                <Input className="flex-1 font-semibold" value={col.title || ""} onChange={(e) => setCols(cols.map((c, j) => j === ci ? { ...c, title: e.target.value } : c))} placeholder="Column title" data-testid={`footer-col-${ci}-title`} />
                <Button size="icon" variant="ghost" disabled={ci === 0} onClick={() => setCols(arrMove(cols, ci, ci - 1))} data-testid={`footer-col-${ci}-up`}><ArrowUp className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" disabled={ci === cols.length - 1} onClick={() => setCols(arrMove(cols, ci, ci + 1))} data-testid={`footer-col-${ci}-down`}><ArrowDown className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" onClick={() => setCols(cols.filter((_, j) => j !== ci))} data-testid={`footer-col-${ci}-remove`}><Trash2 className="h-4 w-4 text-maroon" /></Button>
              </div>
              <div className="mt-2 space-y-2">
                {(col.links || []).map((ln, li) => (
                  <LinkRow key={li} ln={ln} testid={`footer-col-${ci}-link-${li}`}
                    onChange={(v) => setColLinks(ci, col.links.map((x, j) => j === li ? v : x))}
                    onRemove={() => setColLinks(ci, col.links.filter((_, j) => j !== li))}
                    onUp={() => setColLinks(ci, arrMove(col.links, li, li - 1))} onDown={() => setColLinks(ci, arrMove(col.links, li, li + 1))}
                    canUp={li > 0} canDown={li < (col.links.length - 1)} />
                ))}
                <Button size="sm" variant="outline" onClick={() => setColLinks(ci, [...(col.links || []), { label: "", href: "/" }])} data-testid={`footer-col-${ci}-add-link`} className="rounded-full text-xs"><Plus className="mr-1 h-3.5 w-3.5" />Add link</Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-border bg-white p-4">
        <div className="flex items-center justify-between"><h4 className="font-accent text-sm font-bold uppercase text-maroon">Legal Links</h4>
          <Button size="sm" variant="outline" onClick={() => setLegal([...legal, { label: "", href: "/" }])} data-testid="footer-add-legal" className="rounded-full text-xs"><Plus className="mr-1 h-3.5 w-3.5" />Add legal link</Button></div>
        <div className="mt-3 space-y-2">
          {legal.map((ln, i) => (
            <LinkRow key={i} ln={ln} testid={`footer-legal-${i}`}
              onChange={(v) => setLegal(legal.map((x, j) => j === i ? v : x))}
              onRemove={() => setLegal(legal.filter((_, j) => j !== i))}
              onUp={() => setLegal(arrMove(legal, i, i - 1))} onDown={() => setLegal(arrMove(legal, i, i + 1))}
              canUp={i > 0} canDown={i < legal.length - 1} />
          ))}
        </div>
      </div>

      <div className="grid gap-3 rounded-xl border border-border bg-white p-4 sm:grid-cols-2">
        <div className="sm:col-span-2"><Label className="text-xs font-semibold">Disclosure (shown above the legal links)</Label><Textarea value={data.disclosure || ""} onChange={(e) => up({ disclosure: e.target.value })} data-testid="footer-disclosure-input" className="mt-1" rows={2} /></div>
        <div><Label className="text-xs font-semibold">Copyright ({"{year}"} is replaced automatically)</Label><Input value={data.copyright || ""} onChange={(e) => up({ copyright: e.target.value })} data-testid="footer-copyright" className="mt-1" /></div>
        <div className="flex items-end gap-4">
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={data.show_contact !== false} onChange={(e) => up({ show_contact: e.target.checked })} data-testid="footer-show-contact" /> Contact column</label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={data.show_social !== false} onChange={(e) => up({ show_social: e.target.checked })} data-testid="footer-show-social" /> Social icons</label>
        </div>
      </div>
      <p className="text-xs text-muted-foreground">Contact details & social links are pulled from <strong>Global Settings</strong>; toggles control whether they appear.</p>
    </div>
  );
}
